<template>
  <div>
  <page-title :heading=heading :subheading=subheading :icon=icon></page-title>
    <v-container>
      <v-layout>
        <v-flex class="ml-2" xs2>
          <v-text-field v-model="year" :label="$t('annualExpensesReport.year')" :rules="rules.yearRules" mask="####" class="fixed-height-textfield" outline></v-text-field>
        </v-flex>
        <v-flex class="ml-2" xs2>
          <v-select v-model="selectedArea" :items="areas" :label="$t('general.areas')" class="fixed-height" item-text="name" item-value="id" solo></v-select>
        </v-flex>
        <v-flex class="ml-2" xs3>
          <v-select v-model="selectedProject" :items="projects" :label="$t('general.projects')" class="fixed-height" item-value="id" item-text="name" solo>
            <template slot="item" slot-scope="data">
              {{ data.item.name }} - {{ data.item.status === 1 ? $t('projects.stat.active') : $t('projects.stat.inactive')}}
            </template>
            <template v-slot:selection="{ item }">
              <div class="ellipsis-2-lines" style="-webkit-box-orient: vertical !important;" :title="item.name">
                {{ item.name }}
              </div>
            </template>
          </v-select>
        </v-flex>
        <v-flex class="ml-2" xs2>
          <v-select v-model="selectedBranch" :items="branches" :label="$t('general.branches')" class="fixed-height" item-text="branch_name" item-value="id" solo></v-select>
        </v-flex>
        <v-flex class="ml-2" xs3>
          <v-select v-model="selectedEmployee" :rules="rules.required" :items="employees" :label="$t('expenseDetailEmployee.employee')" class="fixed-height" item-text="name" item-value="id" solo>
            <template v-slot:selection="{ item }">
              <div class="ellipsis-2-lines" style="-webkit-box-orient: vertical !important;" :title="item.name">
                {{ item.name }}
              </div>
            </template>
          </v-select>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex class="ml-2" xs4>
          <v-text-field v-model="searchReport" :label="$t('general.search')" clearable append-outer-icon="search"></v-text-field>
        </v-flex>
        <v-flex class="ml-2" xs2>
          <v-btn @click="search()" :loading="loadingItems" :disabled="loading" color="primary" round depressed>
            <v-icon>search</v-icon>
            {{ $t('general.search') }}
          </v-btn>
        </v-flex>
        <v-flex v-if="show && itemsTable.length != 0" class="text-sm-right" xs3>
          <v-btn @click="downloadReport()" :loading="loadingDownload" color="primary" round outline>
            {{$t('expenseDetailEmployee.download')}}
            <v-icon right>cloud_download</v-icon>
          </v-btn>
        </v-flex>
        <v-flex v-if="show && itemsTable.length != 0" class="text-sm-right" xs3>
          <v-btn @click="downloadZipDialog = true" color="primary" round outline>
            {{$t('general.downloadExpensesFiles')}}
            <v-icon right>get_app</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
      <v-layout v-if="loadingItems">
        <v-flex>
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <span>{{$t('general.loadingText')}}</span>
        </v-flex>
      </v-layout>
      <v-layout class="mt-3" v-if="show && !loadingItems">
        <v-flex>
          <v-data-table
            :search="searchReport"
            hide-actions
            :headers="headers"
            :items="itemsTable"
            :loading="loadingItems"
            class="elevation-1"
            :no-data-text="$t('expenseDetailEmployee.noResults')">

            <template v-slot:no-data v-if="itemsTable.length > 0">
              <center class="subtitle font-weight-light">{{$t('general.noDataText')}}</center>
            </template>

            <template v-slot:headers="props">
              <tr class="bg-heavy-rain">
                <th
                  v-for="header in props.headers"
                  :class="['column']"
                  :key="header.text"
                >
                  <h2 class="font-weight-black primary--text mb-0">{{header.text}}</h2>
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <td class="text-xs-center">{{ props.item.name }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[0]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[1]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[2]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[3]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[4]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[5]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[6]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[7]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[8]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[9]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[10]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.months[11]) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.totalTip) }}</td>
              <td class="text-xs-center">{{ currencyFormat(props.item.totalCategory, props.item.totalTip) }}</td>
            </template>
            <template v-slot:footer>
              <tr>
                <td class="text-center"><strong>{{$t('general.total')}}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[0]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[1]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[2]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[3]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[4]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[5]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[6]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[7]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[8]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[9]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[10]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[11]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[12]) }}</strong></td>
                <td class="text-center"><strong>{{ currencyFormat(totalMonths[13]) }}</strong></td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
      <v-dialog
        v-model="downloadZipDialog"
        persistent
        max-width="600px"
      >
        <v-card>
          <v-toolbar flat>
            <h2 class="font-weight-black primary--text mb-0">{{$t('general.downloadExpensesFiles')}}</h2>
          </v-toolbar>
          <v-card-text>
            <v-container>
              <v-form
              ref="valDownload"
              lazy-validation
              >
              <v-layout wrap>
                <v-flex xs12>
                  <v-select
                    :items="zipsAvailables"
                    item-value="month"
                    v-model="selectedZip"
                    :label="$t('general.availableFiles')"
                    return-object
                    :rules="rules.required"
                    required
                  >
                    <template slot="selection" slot-scope="data">
                      ({{year + '-' + data.item.month}}) {{$t('general.expensesFiles')}}
                    </template>
                    <template slot="item" slot-scope="data">
                      ({{year + '-' + data.item.month}}) {{$t('general.expensesFiles')}}
                    </template>
                  </v-select>
                </v-flex>
              </v-layout>
              </v-form>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              flat
              round
              @click="closeDownloadDialog"
            >{{$t('general.close')}}</v-btn>
            <v-btn
              color="primary"
              depressed
              round
              @click="downloadFiles()"
              :loading="loading"
              :disabled="loading"
            >{{$t('general.download')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'annualExpensesReportComponent',
  data () {
    return {
      searchReport: '',
      dialog: false,
      loading: false,
      loadingItems: true,
      expenseItems: [],
      categories: [],
      year: new Date().getFullYear(),
      companyId: null,
      link: '',
      userLogged: {},
      itemsTable: [],
      url: '',
      show: false,
      projects: [],
      selectedProject: null,
      selectedBranch: null,
      selectedArea: null,
      selectedEmployee: null,
      switchAllProjects: true,
      branches: [],
      switchAllBranches: true,
      areas: [],
      employees: [],
      switchAllAreas: true,
      switchAllEmployees: true,
      downloadZipDialog: false,
      totalMonths: [],
      detailedData: [],
      expensesItems: null,
      selectedZip: null,
      valDownload: true,
      loadingDownload: false
    }
  },
  watch: {
    '$store.state.userLogged' () {
      this.getLogged()
    },
    areas () {
      if (this.areas !== undefined && this.areas.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, name: this.$t('expenseDetailEmployee.allAreas') }
        this.selectedArea = -1
        this.areas.unshift(objDefault)
      }
    },
    employees () {
      if (this.employees !== undefined && this.employees.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, name: this.$t('expenseDetailEmployee.allEmployees') }
        this.selectedEmployee = -1
        this.employees.unshift(objDefault)
      }
    },
    projects () {
      if (this.projects !== undefined && this.projects.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, name: this.$t('expenseDetailEmployee.allProjects') }
        this.selectedProject = -1
        this.projects.unshift(objDefault)
      }
    },
    branches () {
      if (this.branches !== undefined && this.branches.find(x => x.id === -1) === undefined) {
        let objDefault = { id: -1, branch_name: this.$t('general.allBranches') }
        this.selectedBranch = -1
        this.branches.unshift(objDefault)
      }
    }
  },
  created () {
    this.getLogged()
  },
  methods: {
    getLogged () {
      if (this.$store.state.userLogged.company) {
        this.userLogged = this.$store.state.userLogged
        this.companyId = this.userLogged.company[0].id
        this.getBranches()
        this.get()
        this.search()
      }
    },
    close () {
      this.dialog = false
    },
    search () {
      this.loadingItems = true
      this.url = `${this.companyId}/${this.year}?type=json`
      if (this.selectedArea !== null && this.selectedArea !== -1) {
        this.url += `&area_id=${this.selectedArea}`
      }
      if (this.selectedBranch !== null && this.selectedBranch !== -1) {
        this.url += `&branch_id=${this.selectedBranch}`
      }
      if (this.selectedProject !== null && this.selectedProject !== -1) {
        this.url += `&project_id=${this.selectedProject}`
      }
      if (this.selectedEmployee !== null && this.selectedEmployee !== -1) {
        this.url += `&employee_id=${this.selectedEmployee}`
      }
      this.getReport()
      this.dialog = false
    },
    get () {
      this.$emit('get', {
        context: this,
        item: {
          companyId: this.companyId
        }
      })
    },
    getReport () {
      this.$emit('getReport', {
        context: this,
        item: {
          url: this.url
        }
      })
    },
    getBranches () {
      this.$emit('getBranches', {
        context: this,
        item: {
          companyId: this.companyId
        }
      })
    },
    downloadReport () {
      this.loadingDownload = true
      var downloadUrl = this.url.replace('json', 'download')
      this.$emit('downloadReport', {
        context: this,
        item: {
          url: downloadUrl
        }
      })
    },
    createItems () {
      this.itemsTable = []
      this.categories.forEach(category => {
        let totalCategory = 0
        let totalTip = 0
        let months = new Array(12)
        for (let i = 0; i < 13; i++) {
          months[i] = 0
        }
        this.expenseItems.reportData.forEach(item => {
          if (category.id === item.gst_categories_id) {
            months[item.month - 1] = item.sumtotal
            totalCategory += parseFloat(item.sumtotal)
            totalTip += parseFloat(item.total_tip_category)
          }
        })
        let itemMod = {
          name: category.name,
          months,
          totalCategory,
          totalTip
        }
        this.itemsTable.push(itemMod)
      })
      this.getTotalMonths()
      this.show = true
    },
    getTotalMonths () {
      let totalArr = []
      let total = 0
      let totalGral = 0
      let totalTip = 0
      for (let i = 0; i < 13; i++) {
        total = 0
        this.itemsTable.forEach(item => {
          total += parseFloat(item.months[i])
        })
        totalArr.push(total)
      }
      this.itemsTable.forEach(item => {
        totalGral += parseFloat(item.totalCategory)
        totalTip += parseFloat(item.totalTip)
      })
      totalArr[12] = totalTip
      totalGral += totalTip
      totalArr.push(totalGral)
      this.totalMonths = totalArr
    },
    currencyFormat (val, tip = null) {
      let tipTotal = 0
      if (tip) {
        tipTotal = tip
      }
      const newVal = val + tipTotal
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      })
      return formatter.format(newVal)
    },
    closeDownloadDialog () {
      this.downloadZipDialog = false
      this.selectedZip = null
    },
    downloadFiles () {
      if (this.$refs.valDownload.validate()) {
        this.$emit('downloadFiles', {
          context: this,
          expensesItems: this.selectedZip,
          date: this.year
        })
        this.loading = true
      }
    }
  },
  computed: {
    heading () {
      return this.$t('annualExpensesReport.name')
    },
    subheading () {
      return this.year.toString()
    },
    icon () {
      return 'far fa-file-alt fa-fw icon-color'
    },
    headers () {
      return [
        {
          text: this.$t('general.category'),
          value: 'name',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.jan'),
          value: 'months[0]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.feb'),
          value: 'months[1]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.mar'),
          value: 'months[2]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.apr'),
          value: 'months[3]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.may'),
          value: 'months[4]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.jun'),
          value: 'months[5]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.jul'),
          value: 'months[6]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.aug'),
          value: 'months[7]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.sep'),
          value: 'months[8]',
          align: 'september',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.oct'),
          value: 'months[9]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.nov'),
          value: 'months[10]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('general.months.dec'),
          value: 'months[11]',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: this.$t('expenses.tip'),
          value: 'totalTip',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        },
        {
          text: 'Total',
          value: 'totalCategory',
          align: 'center',
          class: 'font-weight-black white--text column primary'
        }
      ]
    },
    rules () {
      return {
        yearRules: [
          v => (v && v.length >= 4) || this.$t('validation.minNumberDigits', {number: 4})
        ]
      }
    },
    zipsAvailables () {
      if (this.expensesItems && this.expensesItems.length > 0) {
        return this.expensesItems
      }
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/annualExpensesReport.component.vue